/**
 *  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useContext, useMemo } from "react";
import { LayoutStateElementExpandable, TLayoutState } from "./LayoutState";
import { LAYOUT_TYPE } from "./LayoutTypes";
import { useLayoutElement } from "./useLayoutElement";

export interface ILayoutContextExpandableEl {
	id: string;
	isExpandable: boolean;
	isExpanded: boolean;
	expand: () => void;
	collapse: () => void;
	toggleExpanded: () => void;
}

export interface ILayoutContext {
	parent?: {
		id: string;
	};
	orientation?: {
		horizontal: boolean;
		vertical: boolean;
	};
	panel?: ILayoutContextExpandableEl;
	getLayoutRect?: () => {
		width: number;
		height: number;
		x: number;
		y: number;
	};
}

export const LayoutContext = React.createContext<ILayoutContext>({});

export function useLayoutContext(): ILayoutContext {
	return useContext(LayoutContext);
}

export interface ILayoutContextProviderProps {
	state: TLayoutState;
	layoutElRef: React.MutableRefObject<HTMLElement>;
}

export const LayoutContextProvider: FC<ILayoutContextProviderProps> = ({
	children,
	state,
	layoutElRef
}) => {
	const upperContext = useLayoutContext();
	const el = useLayoutElement(state);

	const getLayoutRect = useCallback<ILayoutContext["getLayoutRect"]>(() => {
		const { width, height, x, y } = layoutElRef.current?.getBoundingClientRect() || {
			width: 0,
			height: 0,
			x: 0,
			y: 0
		};
		return {
			x,
			y,
			width,
			height
		};
	}, [ layoutElRef ]);

	const contextValue = useMemo<ILayoutContext>(() => {
		const contextUpdate: ILayoutContext = {
			...upperContext,
			parent: {
				id: state.id
			}
		};

		if (state.type === LAYOUT_TYPE.COLUMN || state.type === LAYOUT_TYPE.ROW) {
			contextUpdate.orientation = {
				horizontal: state.type === LAYOUT_TYPE.ROW,
				vertical: state.type === LAYOUT_TYPE.COLUMN
			};
		}

		if (el instanceof LayoutStateElementExpandable) {
			if (el.type === LAYOUT_TYPE.PANEL) {
				contextUpdate.panel = {
					id: state.id,
					isExpandable: el.isExpandable,
					isExpanded: el.isExpanded,
					expand: () => {
						el.expand();
					},
					collapse: () => {
						el.collapse();
					},
					toggleExpanded: () => {
						el.toggleExpanded();
					}
				};
			}
		}

		return {
			...upperContext,
			...contextUpdate,
			getLayoutRect
		};
	}, [ state, upperContext, el, getLayoutRect ]);

	return <LayoutContext.Provider value={contextValue}>{children}</LayoutContext.Provider>;
};
